import Sidebar from "./Sidebar";
import MobileNav from "./MobileNav";
import ResultsTicker from "./ResultsTicker";
import { LanguageToggle } from "./LanguageToggle";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { SignOut, Crown } from "@phosphor-icons/react";
import PushNotificationPrompt from "./PushNotificationPrompt";
import ChatFAB from "./ChatFAB";
import AdminCommunityFAB from "./AdminCommunityFAB";
import SupportChatWidget from "./SupportChatWidget";
import SubscriptionExpiryBanner from "./SubscriptionExpiryBanner";
import FlashPickBanner from "./FlashPickBanner";
import PickVipFeaturedModal from "./PickVipFeaturedModal";

function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div
      className="flex-shrink-0 flex items-center justify-between px-4 lg:px-6 py-2.5"
      style={{
        background: "rgba(10,10,12,0.98)",
        backdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        position: "sticky",
        top: 0,
        zIndex: 40,
      }}
    >
      {/* Left: plan badge */}
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-xs flex-shrink-0">
          {(user?.name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="hidden sm:block">
          <span className="text-xs font-semibold text-white">{user?.name}</span>
          {user?.plan === "vip" && (
            <span className="ml-2 inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.3)" }}>
              <Crown weight="fill" className="w-2.5 h-2.5" /> VIP
            </span>
          )}
        </div>
      </div>

      {/* Right: language + logout */}
      <div className="flex items-center gap-2">
        <LanguageToggle />
        <button
          onClick={handleLogout}
          data-testid="topbar-logout"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
          style={{
            background: "rgba(239,68,68,0.12)",
            color: "#F87171",
            border: "1px solid rgba(239,68,68,0.25)",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = "rgba(239,68,68,0.22)";
            e.currentTarget.style.borderColor = "rgba(239,68,68,0.45)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = "rgba(239,68,68,0.12)";
            e.currentTarget.style.borderColor = "rgba(239,68,68,0.25)";
          }}
        >
          <SignOut weight="bold" className="w-3.5 h-3.5" />
          <span>Salir</span>
        </button>
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  return (
    /*
     * Layout "Stripe-style":
     *   - Toda la pantalla = 100dvh (sin scroll del body)
     *   - ResultsTicker fijo arriba
     *   - Sidebar siempre visible a la izquierda (scroll interno si necesita)
     *   - TopBar siempre visible encima del contenido
     *   - Solo el <main> scrollea
     */
    <div style={{
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      background: "#0A0A0C",
      width: "100%",
      maxWidth: "100vw",
      position: "relative",
    }}>
      {/* Ticker fijo — no scrollea */}
      <ResultsTicker />

      {/* Fila principal: Sidebar + contenido */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0, maxWidth: "100vw", width: "100%" }}>

        {/* Sidebar — sticky izquierda, scroll interno si los links no caben */}
        <Sidebar />

        {/* Columna derecha: TopBar fija + contenido scrolleable */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0, maxWidth: "100vw", width: "100%" }}>
          <TopBar />

          {/* Banner global de suscripción por vencer */}
          <SubscriptionExpiryBanner />

          {/* Banner Flash Pick — aparece cuando hay un pick urgente activo */}
          <FlashPickBanner />

          <div className="px-4 lg:px-6 pt-2 flex-shrink-0">
            <PushNotificationPrompt />
          </div>

          {/* Único área que scrollea */}
          <main
            style={{ flex: 1, overflowY: "auto", overflowX: "hidden", minHeight: 0 }}
            className="pb-20 lg:pb-6"
          >
            {/* iOS Safari fix: overflow-x:hidden + overflow-y:auto on same element
                causes Safari to ignore the X clip. This wrapper forces proper containment. */}
            <div style={{ overflowX: "hidden", width: "100%", minWidth: 0, maxWidth: "100vw" }}>
              {children}
            </div>
          </main>
        </div>
      </div>

      {/* Nav móvil — siempre al fondo */}
      <MobileNav />
      <ChatFAB />
      <PickVipFeaturedModal />
      <AdminCommunityFAB />
      <SupportChatWidget />
    </div>
  );
}
